/**
 * CoverageMatrix — EDX-2, re-skinned as the READINESS CONSOLE (round4).
 *
 * The Site Evidence tab's anchor surface post-EDX. The matrix asks one
 * question per row — "do we have enough evidence to cover requirement
 * X?" — and never shows filenames inside the grid, so the cognitive
 * load drops from "scan 30+ paths" to "scan N sufficiency chips".
 *
 * Round4 readiness-console layout (docs/design/round4-coverage-matrix.html):
 *   - Console head — ring gauge (% READY = covered/total), the status-mix
 *     line, a thin top-edge distribution strip, and a "Next gap to close"
 *     jump card. All derived from the SAME rows the lanes render, so the
 *     gauge can never disagree with the list below it. This absorbs the
 *     gauge + critical-gap duties of the old EvidenceReadinessHeader
 *     mount (the component itself is kept for other call sites).
 *   - Triage controls — segmented "Gaps first / All / Complete" filter
 *     with live counts + a search box. "Gaps first" floats Missing /
 *     Tentative rows to the top of each lane; "Complete" narrows to
 *     covered rows; search matches label / description / id.
 *   - Category LANES (domain groups) — collapsible sections with a
 *     stacked roll-up coverage bar + covered-% per lane. Lanes with gaps
 *     default-expanded; fully-covered lanes default-collapsed. Collapsed
 *     / filtered rows stay mounted (hidden) so deep-links + test ids
 *     keep resolving.
 *   - Requirement rows — status glyph + name + provenance subtitle +
 *     mini field-fill meter (filled/total) + chevron, with a selected-row
 *     left-accent treatment.
 *   - Keyboard triage — J/K (or arrows) move the row selection,
 *     Enter/ArrowRight opens the sticky worksheet; a visible kbd hint
 *     line documents it.
 *
 * Chip palette mirrors DCT's RankedDocChip sufficiency tokens so the
 * matrix reads as a derivative of the chip surface, not a parallel
 * design language. See `src/components/siterag/docChecklist/sufficiency.ts`
 * for the canonical sufficiency thresholds.
 *
 * Bridge handlers (`onOpenInDocuments`, `onOpenUploadFor`,
 * `onOpenBaseline`) write `?focus=req:<id>` / `?focus=upload-for:<id>`
 * URL params through the host (SiteLandingPage) so deep-linking +
 * cross-tab breadcrumbs follow the existing SET-B contract. Wiring is
 * receiver-side (EDX-8); this component is just the writer.
 *
 * The matrix itself contains NO filename text — the regression that
 * kills layout density is `path-as-cell-label`; we keep filenames in
 * tooltips only.
 */
import React from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Search, RefreshCw, History } from 'lucide-react';
import { Button } from '../../ui/button';
import { StatusPill } from '../../ui/status-pill';
import { formatRelativeTime } from '../../../lib/portfolio/format';
import type { SiteDocument, SiteRagReport, ProjectIntent, TypedPreferencesByType } from '../../../types/siteRag';
import type { ReportTypeId } from '../../../config/reportTypes';
import { getCoverageScopeReportTypes } from '../../../config/reportTypes';
import {
  getDocumentChecklistAcross,
  type ChecklistItem,
  type RankedChecklistMatch,
  type RankedDocMatch,
} from '../../../config/documentChecklists';
import { computeSufficiency, type DocSufficiency } from '../docChecklist/sufficiency';
// UPE-3 — tier-aware cell status that walks Authored → SED-slot →
// DCT-match in order; replaces the legacy doc-match-score-only path.
import { computeRequirementStatus } from '../../../lib/coverage/requirementStatus';
import { evidenceRequirementForStage, evidenceFieldsWithRecordedUnknowns, evidenceResolvedFieldsForStage } from '../../../lib/coverage/stageEvidenceFields';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import type { ExtractedClaim } from '../../../types/siteContext';
import type { DatacrateModel } from '../../../types/datacrateModel';
import { AuthorEvidenceDialog } from './AuthorEvidenceDialog';
// EVL-1 — consolidated lineage projection (sources × tiers × DAM ×
// site research × prior reports + prompt-inclusion). Same function
// drives EVL-2 (Author dialog pre-fill), EVL-3 (per-row chip strip),
// EVL-4 (Inspector modal), EVL-5 ("Generation will use" tab).
import { getRequirementLineage, type RequirementLineage } from '../../../lib/evidenceLineage/requirementLineage';
import { sedToText } from '../../../services/siteEvidence';
import { RequirementLineageInspector } from './RequirementLineageInspector';
// CAF-3 — tier attribution footer. Reads `lineage.sources.sedSlots[*]
// .status` to surface "filled via DAM / AI Research / Provider"
// attribution in a summary line under the legacy counts.
import {
  attributeRowTier,
  CoverageTierFooter,
  type CoverageRowTier,
} from './CoverageTierAttribution';
import type { RequirementCoverage } from '../../../lib/coverage/requirementCoverage';
import { CoverageRequirementPanel } from './CoverageRequirementPanel';
import { EvidenceStageSelector } from './EvidenceStageSelector';
import {
  CELL_BAR_CLASS,
  CELL_CLASS,
  CELL_GLYPH,
  CELL_LABEL,
  CELL_STATUS_ORDER,
  CELL_DOT_CLASS,
  type CoverageCellStatus,
} from './coverageMatrixUi';
import { rowActionHint } from '../../../lib/coverage/rowActionHint';
import { resolveAutomaticReportFields } from '../../../lib/coverage/resolveAutomaticReportFields';
import { resolveReportSetupFields } from '../../../lib/coverage/resolveReportSetupFields';
import { isNonDocumentRequirement } from '../../../config/reportSetupRequirements';
import {
  requirementDisplayDomain,
  groupRequirementsByDomain,
  type CoverageRequirementDomain,
} from '../../../lib/coverage/requirementDomainGroups';
import { filterRequirementsByIntent } from '../../../lib/coverage/requirementIntentRelevance';
import type { RequirementAutoFill, RequirementAutoFillRegistry } from '../../../lib/coverage/compileCoverageAutoFill';
import { compileCoverageAutoFillFromSed } from '../../../lib/coverage/compileCoverageAutoFill';
import { unresolvedConflictsForRequirement } from '../../../lib/coverage/requirementConflictCaps';
// SED-FOUNDATION — read-only projection of `SED.workflows` (open-questions,
// verification status, siting notes) for the per-row chip + footer summary.
import {
  summarizeWorkflows,
  rollupSectionsForRequirement,
  type WorkflowSummary,
} from '../../../lib/sed/workflowSummary';

/**
 * UAE-5 — Coverage Matrix cell status union extended with `'authored'`.
 * An authored row sits between `tentative` and `adequate`: a structured
 * authored claim with `high` confidence ranks as `adequate`; structured
 * `high` AND the claim text has 80%+ field coverage ranks as `strong`.
 */
export type { CoverageCellStatus } from './coverageMatrixUi';

export interface CoverageMatrixProps {
  /** All site documents — used to resolve docId → SiteDocument for tooltips. */
  documents: SiteDocument[];
  /** Site id — passed through to bridge handlers; not used internally. */
  siteId: string;
  /**
   * UPE-3 — the live SED. Walked by `computeRequirementStatus`'s
   * Tier 2 (slot-aware) path to verify that the requirement's
   * sections were actually populated, not just that a DCT match
   * score exists. Optional so legacy hosts that don't yet have the
   * SED loaded keep rendering (falls through to Tier 3 DCT
   * fallback — same as pre-UPE behaviour).
   */
  sed?: SiteEvidenceDocument;
  /** Report types in scope (typically `site.reports[].reportType` deduped). */
  scopeReportTypes: ReportTypeId[];
  /**
   * The site's decision stage. During early screening (1) a site with no
   * reports plans only Stage 1 requirements; absent → the full planning set.
   */
  decisionStage?: 1 | 2;
  /** Review-view switch only; never changes the canonical decision stage. */
  onEvidenceStageChange?: (stage: 1 | 2) => void;
  /** The host mounts the same stage control above all Evidence panes. */
  hideStageSelector?: boolean;
  /** Whether the SED is out of date. Drives the 'Rerun' notification tag. */
  isStaleSed?: boolean;
  /**
   * ISO timestamp of the last successful SED bootstrap (`site.lastBootstrapAt`,
   * written by `bootstrapSiteSedServer`). Surfaced as a neutral "rebuilt N
   * ago" freshness chip beside the SED version badge so the user can tell at
   * a glance how recently the evidence build was refreshed. Optional —
   * legacy sites without the timestamp omit the chip (no regression).
   */
  sedRebuiltAt?: string;
  /** Per-report-type ranked-match results from DCT's
   *  `matchUploadedDocsToChecklistRanked` (host-owned because the
   *  matcher is async + cached and the parent also feeds it to the
   *  Author-evidence lineage prefill). */
  matchByType: Map<ReportTypeId, RankedChecklistMatch>;
  /** Optional — DM5 user-curated disconnects. Threaded through for
   *  inline rationale; the matcher already excludes disconnected pairs
   *  upstream. */
  disconnectedLinks?: ReadonlyArray<{ docId: string; targetId: string }>;
  /** Bridge — open Documents tab pre-filtered to the requirement.
   *  Receives the checklist item id (e.g. 'geotech', 'phase1-esa'). */
  onOpenInDocuments: (requirementId: string) => void;
  /** Bridge — open Documents tab upload widget with the requirement pre-tagged. */
  onOpenUploadFor: (requirementId: string) => void;
  /** Bridge — open the Tier-3 baseline confirm dialog for the requirement. */
  onOpenBaseline?: (requirementId: string) => void;
  /**
   * UAE-2 — user-authored claims persisted on `site.siteContext.claims[]`.
   * Walked to flip a row's cell status to `'authored'` when a claim's
   * `requirementId` matches the row's `ChecklistItem.id`. Optional —
   * legacy sites without a site-context still render with the old
   * four-status palette.
   */
  authoredClaims?: readonly ExtractedClaim[];
  /**
   * UAE-2 — opt-out hook for hosts that want to manage the dialog
   * themselves (e.g. a future Author panel that lives in Documents).
   * When omitted, the matrix mounts its own `AuthorEvidenceDialog`
   * internally on `[Author]` click. The hook is fired regardless so
   * external callers can also react to the click (audit / focus).
   */
  onOpenAuthorFor?: (requirementId: string) => void;
  /**
   * When true, the host renders `AuthorEvidenceDialog` (e.g. at the
   * SiteEvidenceTab level via portal). The matrix only fires
   * `onOpenAuthorFor` and does not mount the dialog internally — keeps
   * the dialog alive across matrix re-renders and overflow clipping.
   */
  hostOwnsAuthorDialog?: boolean;
  /**
   * UAE-2 — fired after an authored evidence write lands successfully.
   * Hosts can refresh their authored-claim list (the matrix derives
   * its own counts from `authoredClaims` so this is purely a hook).
   */
  onAuthoredClaimSaved?: (result: { claimId: string; requirementId: string }) => void;
  /**
   * CMU-1 — fired when the user clicks `[Replace]` on a populated row.
   * Carries the docId of the existing top-match (the doc being
   * replaced) AND the requirementId (so the receiver can pre-filter
   * the Documents tab + carry the requirement-intent through to the
   * new upload). Host typically jumps to the Documents tab with a
   * `?focus=replace:<docId>` param.
   *
   * Optional — legacy hosts without a replace receiver still render
   * the matrix fine (the button no-ops if not provided).
   */
  onReplaceDoc?: (docId: string, requirementId: string) => void;
  /**
   * CMU-1 — fired when the user clicks `[+ Add supporting]` on a
   * populated row. Carries the requirementId; the host runs the same
   * upload-pre-tagged flow as `onOpenUploadFor` but the label reads
   * "Add supporting document" so the user understands the upload
   * STRENGTHENS the existing coverage rather than replacing the
   * current top match.
   *
   * When omitted, the button falls back to `onOpenUploadFor` so the
   * affordance keeps working on legacy hosts.
   */
  onAddSupportingDoc?: (requirementId: string) => void;
  /**
   * EVL-1 — optional DAM passed through to the lineage projection so
   * the chip strip + Inspector modal can surface DAM-reference counts.
   * When omitted the lineage projection skips the DAM channel (no
   * regression vs. legacy hosts that don't load the DAM upfront).
   */
  dam?: DatacrateModel | null;
  /**
   * EVL-1 — optional prior reports for the RDX bridge. Empty until
   * RDX writes `reportClaims/` per site. The lineage projection
   * iterates this array; legacy hosts pass `undefined`.
   */
  priorReports?: ReadonlyArray<SiteRagReport>;
  /**
   * EVL-3 — fired when the user clicks the chip-strip "Show evidence
   * chain" affordance. Hosts mount `EvidenceLineageInspector` against
   * the supplied lineage. When omitted, the chip strip renders but
   * the click is a no-op (graceful fallback).
   */
  onOpenLineage?: (lineage: RequirementLineage) => void;
  /**
   * CAF-4 — per-requirement coverage envelope keyed by
   * `ChecklistItem.id`. Produced by the CAF-5 pipeline writer
   * (`resolveRequirementCoverage` walked over every checklist row
   * and stamped on `site.coverage`). Optional — legacy sites
   * without a writer run keep rendering; the chip falls through to
   * the "no envelope yet" muted state.
   */
  coverageByRequirement?: Record<string, RequirementCoverage>;
  /**
   * Master–detail — switch Extracted Variables tab filtered to this
   * requirement's field paths.
   */
  onViewVariables?: (requirementId: string, fieldPath?: string) => void;
  /** In-panel variable highlight when unified evidence panel is on. */
  panelFocusVariablePath?: string | null;
  /** Force-expand the in-panel variables accordion. */
  forceExpandRequirementVariables?: boolean;
  /** Auto-select matrix row when deep-linking a variable/conflict (unified panel). */
  focusRequirementId?: string | null;
  siteContext?: import('../../../types/siteContext').SiteContext | null;
  /** Site project intent — ranks report fields in the detail panel. */
  projectIntent?: ProjectIntent | null;
  onOpenDocument?: (docId: string) => void;
  onReloadSed?: () => void;
  unifiedEvidencePanel?: boolean;
  /** Function to trigger SED rebuild from the search bar row. */
  onRebuildSed?: () => void;
  renderTopicSourceDetails?: (requirementId: string) => React.ReactNode;
  /** Whether a SED rebuild is currently active. */
  isRebuilding?: boolean;
  /**
   * Reports field-level completeness up to the Evidence Ledger, derived from
   * the SAME rows the lanes render. Lets the ledger show what the evidence
   * actually contains instead of the ring's covered/total, which counts a
   * zero-source, zero-field "Adequate" row as fully covered.
   *
   * Must be a STABLE reference (useCallback) — it is an effect dependency.
   */
  onEvidenceSummaryChange?: (summary: {
    filled: number;
    total: number;
    ready: number;
    partial: number;
    empty: number;
  }) => void;
  /**
   * Suppress the readiness console head (ring, status-mix, next-gap card,
   * distribution strip) because the Evidence Ledger above already owns the
   * "how ready is this site?" question — and answers it with field fill
   * rather than the ring's inflated covered/total. Set by the Site Evidence
   * tab whenever the ledger renders; other hosts keep the console.
   */
  hideReadinessConsole?: boolean;
  /**
   * CAR-6 — optional provider auto-fill registry. When supplied, the
   * populate actions merge registry entries after lineage seed.
   */
  autoFillRegistry?: RequirementAutoFillRegistry;
  /** Live supply-chain plan — drives vendor-quote-pack auto-fill. */
  supplyChainPlan?: import('../../../types/supplyChain').SupplyChainPlan | null;
  /** Site environmental typed prefs — seeds species/cultural/discharge prefill. */
  environmentalPreferences?: import('../../../types/siteRag').EnvironmentalPreferences | null;
  /** Full site typed preference defaults — drives setup row resolution. */
  typedPreferences?: TypedPreferencesByType;
  /** Opens Site Project Intent editor from intent setup row. */
  onOpenSiteIntent?: (fieldKey?: string) => void;
  /**
   * Requirement waivers explicitely recorded by the user. Keyed by `requirementId`.
   * Row status evaluates to `'waived'` if a waiver exists.
   */
  requirementWaivers?: Record<string, import('../../../types/site').SiteRequirementWaiver>;
  /** Waive this requirement explicitly (e.g. accepted gap). */
  onWaiveRequirement?: (requirementId: string, reason: string, category: 'not-applicable' | 'data-unavailable' | 'accepted-gap' | 'will-provide') => Promise<void>;
}

/** Per-cell summary, after resolving the per-type ranked match. */
interface CellState {
  status: CoverageCellStatus;
  /** Top match for the cell (highest score). Drives the hover tooltip. */
  topMatch: { doc: SiteDocument; row: RankedDocMatch; sufficiency: DocSufficiency } | null;
  /** Total qualifying docs (Primary + Supporting). */
  matchCount: number;
  /**
   * UAE-2 — authored claims whose `requirementId` matches this row.
   * Drives the `'authored'` chip + tooltip. Empty when no claim has
   * been authored against the requirement.
   */
  authoredClaims?: readonly ExtractedClaim[];
}

/** Pick the best status across an array of cell states for the row's
 *  roll-up. Strong > Adequate > Authored > Tentative > Missing.
 *  Authored ranks below Adequate by default so a doc-derived Adequate
 *  cell trumps a `low`-confidence authored claim; the per-cell upgrade
 *  to `adequate`/`strong` ranks (see `resolveAuthoredStatus`) bumps
 *  high-confidence structured authorings into the Adequate / Strong
 *  bands. */
const STATUS_RANK: Record<CoverageCellStatus, number> = {
  strong: 4,
  adequate: 3,
  authored: 2,
  tentative: 1,
  missing: 0,
  waived: 0,
};
function bestOf(cells: CellState[]): CellState {
  if (cells.length === 0) return { status: 'missing', topMatch: null, matchCount: 0 };
  let best = cells[0]!;
  for (const c of cells) {
    if (STATUS_RANK[c.status] > STATUS_RANK[best.status]) best = c;
  }
  return best;
}

/** Statuses that count as a triage GAP (not yet covered by real evidence).
 *  Mirrors EvidenceReadinessHeader's `COVERED` complement so the console
 *  gauge keeps the exact same semantics the old header gauge had. */
const GAP_STATUSES: ReadonlySet<CoverageCellStatus> = new Set(['tentative', 'missing']);

/**
 * Resolve a single (requirement, report-type) cell.
 *
 * UPE-3 — status is computed via `computeRequirementStatus`'s
 * three-tier walk:
 *
 *   Tier 1 — authored evidence wins above all else (user vouched).
 *   Tier 2 — SED slot populated by doc extraction (real evidence).
 *   Tier 3 — DCT match fallback, capped at `'tentative'`.
 *
 * The legacy `computeSufficiency` (per-doc thickness signal) still
 * runs for the tooltip + the `topMatch` payload — knowing the top
 * doc's filename and sufficiency helps users orient. But the cell
 * STATUS no longer derives from it. This is the core fix for UPE
 * Cliff 1 — a high DCT score on a doc that didn't populate any slot
 * can no longer read `'strong'`.
 */
function resolveCell(
  requirement: ChecklistItem,
  match: RankedChecklistMatch | undefined,
  documents: SiteDocument[],
  authoredClaims: readonly ExtractedClaim[] | undefined,
  sed: SiteEvidenceDocument | undefined,
  projectIntent: ProjectIntent | undefined,
  typedPreferences: TypedPreferencesByType | undefined,
  reportType: ReportTypeId | undefined,
  waiver: import('../../../types/site').SiteRequirementWaiver | undefined,
  decisionStage?: 1 | 2,
): CellState {
  const claimsForReq = (authoredClaims ?? []).filter(c => c.requirementId === requirement.id);

  // ── Compute the canonical status via the tier-aware walk ────────
  let tieredStatus = computeRequirementStatus({
    requirement,
    sed,
    documents,
    authoredClaims,
    match,
    projectIntent,
    typedPreferences,
    reportType,
  });
  // Review completion is not physical-data completeness. The stage view may
  // show recorded unknowns as answered, but they can never raise Strong.
  if (decisionStage != null && requirement.kind === 'site-intent') {
    const known = evidenceFieldsWithRecordedUnknowns(resolveReportSetupFields({
      requirement, projectIntent, typedPreferences, reportType,
    }), requirement.id, projectIntent, decisionStage);
    if ((known.unresolvedFieldKeys?.length ?? 0) > 0) {
      if (known.structuredFilled === 0 && tieredStatus !== 'missing') tieredStatus = 'tentative';
      else if (tieredStatus === 'strong' || tieredStatus === 'authored-strong') tieredStatus = 'adequate';
    }
  }
  // Map the tier-aware union to the CoverageCellStatus union the
  // matrix already paints. Authored variants collapse to the existing
  // `'authored'` cell colour; `'authored-strong'` re-uses the same
  // palette but the rank-up via STATUS_RANK makes it bubble up to
  // the row roll-up appropriately.
  const status: CoverageCellStatus = (() => {
    if (waiver) return 'waived';
    switch (tieredStatus) {
      case 'authored-strong':
      case 'authored':    return 'authored';
      case 'strong':      return 'strong';
      case 'adequate':    return 'adequate';
      case 'tentative':   return 'tentative';
      case 'missing':     return 'missing';
    }
  })();

  // ── Resolve the top doc for the tooltip + matchCount ────────────
  // (the cell status no longer depends on this — only the tooltip
  //  and the rendered "N docs" footer text do).
  let topMatch: CellState['topMatch'] = null;
  let matchCount = 0;
  if (match) {
    const rows = match.matched.get(requirement.id);
    if (rows && rows.length > 0) {
      matchCount = rows.length;
      const top = rows[0]!;
      const doc = documents.find(d => d.id === top.docId);
      if (doc) {
        topMatch = { doc, row: top, sufficiency: computeSufficiency(doc, top.score) };
      }
    }
  }

  return {
    status,
    topMatch,
    matchCount,
    authoredClaims: claimsForReq.length > 0 ? claimsForReq : undefined,
  };
}

/**
 * Per-row field-fill fraction for the mini meter — the same
 * `statusFilled/statusTotal` the worksheet's CompletenessBar reads, so
 * the row meter and the worksheet progress bar can never disagree.
 */
function resolveRowFill(
  item: ChecklistItem,
  claim: ExtractedClaim | undefined,
  sed: SiteEvidenceDocument | undefined,
  projectIntent: ProjectIntent | undefined,
  typedPreferences: TypedPreferencesByType | undefined,
  reportType: ReportTypeId | undefined,
  decisionStage?: 1 | 2,
  autoFillEntry?: RequirementAutoFill,
): { filled: number; total: number } | null {
  const raw = isNonDocumentRequirement(item)
    ? resolveReportSetupFields({
      requirement: item,
      reportType,
      projectIntent,
      typedPreferences,
    })
    : resolveAutomaticReportFields({
      requirement: item,
      sed,
      authoredClaim: claim,
      autoFillEntry,
    });
  const resolved = evidenceFieldsWithRecordedUnknowns(evidenceResolvedFieldsForStage(raw, item.id, decisionStage), item.id, projectIntent, decisionStage);
  if (resolved.statusTotal > 0) {
    return { filled: resolved.statusFilled, total: resolved.statusTotal };
  }
  return null;
}

/** Minimal row shape `pickNextGap` needs — exported for unit tests. */
export interface NextGapCandidate {
  item: Pick<ChecklistItem, 'id' | 'label' | 'severity'>;
  best: { status: CoverageCellStatus };
  fill: { filled: number; total: number } | null;
}

/**
 * "Next gap to close" picker for the console jump card.
 *
 * Priority (composed from the existing readiness primitives — see the
 * EvidenceReadinessHeader criticalGaps walk + STATUS_RANK):
 *   1. The first critical-severity gap in display order (GO-blocking).
 *   2. Otherwise the worst-status gap (missing before tentative),
 *      tie-broken by the highest field-fill fraction — the gap closest
 *      to an upgrade is the highest-leverage one to close next.
 * Returns null when no row is tentative/missing (all-clear).
 */
export function pickNextGap<T extends NextGapCandidate>(rows: readonly T[]): T | null {
  const gaps = rows.filter(r => GAP_STATUSES.has(r.best.status));
  if (gaps.length === 0) return null;
  const critical = gaps.find(r => r.item.severity === 'critical');
  if (critical) return critical;
  return [...gaps].sort((a, b) => {
    const byRank = STATUS_RANK[a.best.status] - STATUS_RANK[b.best.status];
    if (byRank !== 0) return byRank;
    const fa = a.fill && a.fill.total > 0 ? a.fill.filled / a.fill.total : 0;
    const fb = b.fill && b.fill.total > 0 ? b.fill.filled / b.fill.total : 0;
    return fb - fa;
  })[0] ?? null;
}

/** Provenance subtitle under the row name — counts only, NEVER filenames. */
function rowSubtitle(best: CellState, lineage: RequirementLineage): string {
  const c = lineage.counts;
  const parts: string[] = [CELL_LABEL[best.status]];
  if (c.authoredClaims > 0) {
    parts.push(`${c.authoredClaims} authored claim${c.authoredClaims === 1 ? '' : 's'}`);
  }
  const sources =
    c.docSources + c.providerSources + c.damSources + c.siteResearchSources + c.priorReportSources;
  if (sources > 0) {
    parts.push(`${sources} source${sources === 1 ? '' : 's'}`);
  } else if (c.authoredClaims === 0) {
    parts.push(best.status === 'missing' ? 'no evidence yet' : 'no linked sources');
  }
  return parts.join(' · ');
}

type TriageFilter = 'gaps' | 'all' | 'complete';

const KBD_CLASS =
  'rounded border border-border-strong bg-surface-1 px-1.5 py-px font-mono text-[10px] text-text-secondary';

/**
 * Console ring gauge — % READY where ready = covered/total and covered
 * mirrors the old EvidenceReadinessHeader semantics (anything that is
 * not tentative/missing). SVG stroke ring (same recipe as the retired
 * header gauge) so the sweep animates via stroke-dasharray and degrades
 * to a static ring under prefers-reduced-motion.
 */
const ReadinessRing: React.FC<{ covered: number; total: number; pct: number }> = ({
  covered,
  total,
  pct,
}) => {
  const RADIUS = 30;
  const STROKE = 6;
  const C = 2 * Math.PI * RADIUS;
  // Sweep-in on mount: first paint at 0, then transition to the real
  // value. `motion-reduce:transition-none` snaps it for reduced motion.
  const [sweepPct, setSweepPct] = React.useState(0);
  React.useEffect(() => {
    const frame = window.requestAnimationFrame(() => setSweepPct(pct));
    return () => window.cancelAnimationFrame(frame);
  }, [pct]);
  const dash = (Math.max(0, Math.min(100, sweepPct)) / 100) * C;
  return (
    <div
      className="relative shrink-0"
      style={{ width: 76, height: 76 }}
      data-testid="coverage-readiness-gauge"
      data-covered={covered}
      data-total={total}
      role="img"
      aria-label={`${pct}% ready — ${covered} of ${total} requirements covered`}
    >
      <svg width={76} height={76} viewBox="0 0 76 76" className="-rotate-90">
        <circle
          cx={38}
          cy={38}
          r={RADIUS}
          fill="none"
          stroke="var(--border)"
          strokeWidth={STROKE}
          opacity={0.5}
        />
        <circle
          cx={38}
          cy={38}
          r={RADIUS}
          fill="none"
          stroke="var(--primary)"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${C}`}
          className="transition-[stroke-dasharray] duration-700 ease-out motion-reduce:transition-none"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className="font-mono text-base font-bold text-text-primary tabular-nums">{pct}%</span>
        <span className="font-mono text-[9px] tracking-[0.12em] text-text-muted">READY</span>
      </div>
    </div>
  );
};

export const CoverageMatrix: React.FC<CoverageMatrixProps> = ({
  documents,
  siteId,
  sed,
  isStaleSed,
  sedRebuiltAt,
  scopeReportTypes,
  decisionStage: suppliedDecisionStage,
  onEvidenceStageChange,
  hideStageSelector = false,
  matchByType,
  onOpenInDocuments,
  onOpenUploadFor,
  onOpenBaseline,
  authoredClaims,
  onOpenAuthorFor,
  hostOwnsAuthorDialog = false,
  onAuthoredClaimSaved,
  onReplaceDoc,
  onAddSupportingDoc,
  dam,
  priorReports,
  onOpenLineage,
  coverageByRequirement,
  onViewVariables,
  panelFocusVariablePath,
  forceExpandRequirementVariables,
  focusRequirementId,
  siteContext,
  projectIntent,
  onOpenDocument,
  onReloadSed,
  unifiedEvidencePanel,
  onRebuildSed,
  renderTopicSourceDetails,
  isRebuilding,
  autoFillRegistry,
  supplyChainPlan,
  environmentalPreferences,
  typedPreferences,
  onOpenSiteIntent,
  requirementWaivers,
  onWaiveRequirement,
  onEvidenceSummaryChange,
  hideReadinessConsole = false,
}) => {
  // Walk the union of requirements across every report type in scope.
  // COVERAGE-CHECK CONSISTENCY — the fallback set (when the site has no
  // reports yet) is resolved by the shared `getCoverageScopeReportTypes`
  // helper so this matrix and the host's ranked DCT matcher walk the SAME
  // requirement set. Previously this surface fell back to
  // `Object.keys(REPORT_TYPE_REGISTRY)` (ALL types, including coming-soon +
  // meta aggregation types) while the since-removed Documents-tab and
  // Overview coverage views used narrower sets, so a planning-stage site
  // read different "missing" counts on each tab.
  const [reviewStageOverride, setReviewStageOverride] = React.useState<{ siteId: string; stage: 1 | 2 } | null>(null);
  const decisionStage = suppliedDecisionStage == null ? undefined
    : onEvidenceStageChange ? suppliedDecisionStage
      : reviewStageOverride?.siteId === siteId ? reviewStageOverride.stage : suppliedDecisionStage;
  const simpleFacts = decisionStage != null;
  const changeReviewStage = (stage: 1 | 2) => {
    if (onEvidenceStageChange) onEvidenceStageChange(stage);
    else setReviewStageOverride({ siteId, stage });
  };

  const effectiveTypes: ReportTypeId[] = React.useMemo(
    () => getCoverageScopeReportTypes(scopeReportTypes, { decisionStage }),
    [scopeReportTypes, decisionStage],
  );

  const canonicalRequirements: ChecklistItem[] = React.useMemo(
    // Intent-scope the requirement set so the grid rows AND the footer status
    // counts ("N strong · N missing") narrow to the intent-relevant set —
    // consistent with the report evidence badge + Overview coverage %. When
    // intent is empty/unknown this is the identity (no regression).
    () => filterRequirementsByIntent(getDocumentChecklistAcross(effectiveTypes), projectIntent),
    [effectiveTypes, projectIntent],
  );
  const requirements: ChecklistItem[] = React.useMemo(
    () => canonicalRequirements.flatMap(item => {
      const scoped = evidenceRequirementForStage(item, decisionStage, projectIntent);
      return scoped ? [scoped] : [];
    }),
    [canonicalRequirements, decisionStage, projectIntent],
  );

  // SED-FOUNDATION — read-only workflow summary. Empty for legacy SEDs (no
  // `workflows` field), so the chip + footer render nothing and existing
  // behaviour is unchanged.
  const workflowSummary: WorkflowSummary = React.useMemo(
    () => summarizeWorkflows(sed?.workflows),
    [sed?.workflows],
  );

  const effectiveAutoFillRegistry = React.useMemo(
    () => autoFillRegistry ?? compileCoverageAutoFillFromSed(sed, {
      supplyChainPlan,
      environmentalPreferences,
      typedPreferences: typedPreferences ?? (environmentalPreferences
        ? { environmental: environmentalPreferences }
        : undefined),
      projectIntent,
      siteContext: siteContext ?? undefined,
    }),
    [autoFillRegistry, sed, supplyChainPlan, environmentalPreferences, typedPreferences, projectIntent, siteContext],
  );

  const sedText = React.useMemo(
    () => {
      if (!sed) return undefined;
      try {
        return sedToText(sed);
      } catch {
        return undefined;
      }
    },
    [sed],
  );

  // UAE-2 — dialog state for the internal AuthorEvidenceDialog. When
  // a parent supplies its own `onOpenAuthorFor` handler we still fire
  // the event but also mount the dialog locally; hosts can opt out by
  // simply ignoring the saved-claim hook.
  const [authoringFor, setAuthoringFor] = React.useState<ChecklistItem | null>(null);
  // EVL-3 — internal state for the "show evidence chain" affordance.
  // Hosts can override the click via `onOpenLineage` to pipe the modal
  // through their own Inspector — when omitted the matrix mounts its own.
  const [inspectingLineage, setInspectingLineage] = React.useState<RequirementLineage | null>(null);
  const [selectedRequirementId, setSelectedRequirementId] = React.useState<string | null>(null);
  // Round4 triage state — segmented filter ('gaps' default per the
  // approved mockup), search query, and per-lane expand/collapse
  // overrides (undefined ⇒ default: lanes with gaps expand, fully
  // covered lanes collapse).
  const [statusFilter, setStatusFilter] = React.useState<ReadonlySet<CoverageCellStatus> | null>(null);
  const [showGoBlockingOnly, setShowGoBlockingOnly] = React.useState(false);
  const [screeningFilter, setScreeningFilter] = React.useState<'all' | 'unknown' | 'conflicts'>('all');

  const toggleStatusFilter = React.useCallback((status: CoverageCellStatus) => {
    setStatusFilter(prev => {
      if (prev === null) return new Set([status]);
      if (prev.size === 1 && prev.has(status)) return null;
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }, []);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [laneOverrides, setLaneOverrides] = React.useState<
    Partial<Record<CoverageRequirementDomain, boolean>>
  >({});
  const rowRefs = React.useRef(new Map<string, HTMLDivElement>());
  const worksheetRef = React.useRef<HTMLDivElement | null>(null);

  /**
   * Select a requirement AND make its worksheet visible.
   *
   * The master-detail grid is two columns only at `xl`. Below that the
   * worksheet stacks under the entire lane list — roughly 700px down with ten
   * rows — so clicking a row selected it silently and the user saw nothing
   * happen (founder live-QA, 2026-07-25). Scroll it into view whenever it is
   * off-screen; no-op when it is already visible (the sticky xl layout), so
   * the wide case is unchanged.
   */
  const selectRequirement = React.useCallback((id: string) => {
    setSelectedRequirementId(id);
    window.requestAnimationFrame(() => {
      const el = worksheetRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const offScreen = r.top > window.innerHeight - 120 || r.bottom < 120;
      if (offScreen) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }, []);
  const handleOpenLineage = React.useCallback(
    (lineage: RequirementLineage) => {
      if (onOpenLineage) {
        onOpenLineage(lineage);
      } else {
        setInspectingLineage(lineage);
      }
    },
    [onOpenLineage],
  );

  // For each requirement, derive its per-type cells + best status.
  type Row = {
    item: ChecklistItem;
    cells: Map<ReportTypeId, CellState>;
    best: CellState;
    /**
     * EVL-1/3 — consolidated lineage projection (authored × SED slots
     * × DCT × DAM × site research × prior reports + prompt-inclusion).
     * Drives the row subtitle counts, the Inspector modal (EVL-4), and
     * the Author dialog pre-fill (EVL-2).
     */
    lineage: RequirementLineage;
    /** Field-fill fraction for the row's mini meter (null = no schema). */
    fill: { filled: number; total: number } | null;
    /** Lane this row renders under — drives deep-link lane expansion. */
    domain: CoverageRequirementDomain;
  };
  const rows: Row[] = React.useMemo(() => {
    // Primary DAM/prompt context retains its existing attribution. DCT
    // lineage separately resolves the requirement's owning type in scope.
    const primaryReportType: ReportTypeId = effectiveTypes[0] ?? 'feasibility';
    return requirements.map(item => {
      const cells = new Map<ReportTypeId, CellState>();
      const cellList: CellState[] = [];
      for (const t of effectiveTypes) {
        const state = resolveCell(
          item,
          matchByType.get(t),
          documents,
          authoredClaims,
          sed,
          projectIntent ?? undefined,
          typedPreferences,
          t,
          requirementWaivers?.[item.id],
          decisionStage,
        );
        cells.set(t, state);
        cellList.push(state);
      }
      const lineage = getRequirementLineage(item.id, {
        site: { siteContext: { claims: authoredClaims ?? [] }, documents },
        sed,
        reportType: primaryReportType,
        scopeReportTypes: effectiveTypes,
        dam: dam ?? undefined,
        matchByType,
        priorReports: priorReports ? Array.from(priorReports) : undefined,
        requirementLabel: item.label,
        checklistItem: item,
      });
      const best = bestOf(cellList);
      const fill = resolveRowFill(
        item,
        best.authoredClaims?.[0],
        sed,
        projectIntent ?? undefined,
        typedPreferences,
        effectiveTypes[0],
        decisionStage,
        effectiveAutoFillRegistry[item.id],
      );
      return { item, cells, best, lineage, fill, domain: requirementDisplayDomain(item) };
    });
  }, [requirements, effectiveTypes, matchByType, documents, authoredClaims, sed, dam, priorReports, projectIntent, typedPreferences, decisionStage, effectiveAutoFillRegistry]);

  // Apply each navigation request once. A retained focus prop must not
  // replay after a report/SED update and replace the topic the user selected.
  // Keep an unavailable target pending until its real requirement arrives.
  const focusRequest = React.useRef<{
    siteId: string;
    requirementId: string;
    applied: boolean;
  } | null>(null);
  React.useEffect(() => {
    if (!focusRequirementId) {
      focusRequest.current = null;
      return;
    }
    if (focusRequest.current?.siteId !== siteId
      || focusRequest.current.requirementId !== focusRequirementId) {
      focusRequest.current = { siteId, requirementId: focusRequirementId, applied: false };
    }
    if (focusRequest.current.applied) return;
    const row = rows.find(r => r.item.id === focusRequirementId);
    if (!row) return;
    focusRequest.current.applied = true;
    setSelectedRequirementId(focusRequirementId);
    setScreeningFilter('all');
    if (statusFilter !== null && !statusFilter.has(row.best.status)) setStatusFilter(null);
    setLaneOverrides(prev =>
      prev[row.domain] === true ? prev : { ...prev, [row.domain]: true },
    );
  }, [siteId, focusRequirementId, rows, statusFilter]);

  React.useEffect(() => {
    // Read the queued selection too, so an initial navigation request is not
    // overwritten by this effect's render-time null selection.
    setSelectedRequirementId(current => {
      if (rows.length === 0) return null;
      if (current != null && rows.some(r => r.item.id === current)) return current;
      const firstNonMissing = rows.find(r => r.best.status !== 'missing');
      return (firstNonMissing ?? rows[0]!).item.id;
    });
  }, [rows]);

  const selectedRow = React.useMemo(
    () => rows.find(r => r.item.id === selectedRequirementId) ?? null,
    [rows, selectedRequirementId],
  );

  const isGapRow = React.useCallback(
    (row: Row) => GAP_STATUSES.has(row.best.status),
    [],
  );

  // Summary counts — drives the console gauge + status-mix line + the
  // one-line footer. UAE-5 — authored rows are bucketed into `authored`.
  const summary = React.useMemo(() => {
    const counts: Record<CoverageCellStatus, number> = {
      strong: 0, adequate: 0, authored: 0, tentative: 0, missing: 0, waived: 0,
    };
    for (const row of rows) counts[row.best.status] += 1;
    const criticalMissing = rows.filter(r =>
      r.best.status === 'missing' && r.item.severity === 'critical',
    );
    return { counts, criticalMissing };
  }, [rows]);

  const gapCount = summary.counts.tentative + summary.counts.missing;
  const covered = rows.length - gapCount;
  const readyPct = rows.length > 0 ? Math.round((covered / rows.length) * 100) : 0;

  /**
   * HONEST COMPLETENESS — reported up to the Evidence Ledger.
   *
   * `readyPct` above counts any row that is not tentative/missing as fully
   * covered, so a row with NO linked sources and 0 of N fields filled still
   * scores as covered. On Redhead Industrial Park that produced "80% READY"
   * for a site whose fields were 76/166 = 46% filled, with two rows badged
   * "Adequate" holding literally no evidence.
   *
   * This projection reports what the fields actually contain, from the SAME
   * `rows` the lanes render, so the ledger's bar can never disagree with the
   * list below it. `readyPct` is deliberately left untouched — changing the
   * ring's own semantics is a separate, wider change (it feeds other
   * readiness consumers).
   */
  const evidenceSummary = React.useMemo(() => {
    let filled = 0;
    let total = 0;
    let ready = 0;
    let partial = 0;
    let empty = 0;
    for (const row of rows) {
      const f = row.fill;
      if (f && f.total > 0) {
        filled += f.filled;
        total += f.total;
      }
      // A row holds NO evidence when it has a schema and none of it is filled.
      const hasNothing = !!f && f.total > 0 && f.filled === 0;
      if (hasNothing) empty += 1;
      else if (row.best.status === 'strong' || row.best.status === 'authored') ready += 1;
      else partial += 1;
    }
    return { filled, total, ready, partial, empty };
  }, [rows]);

  // Report upward. Depends on the memo identity, so it fires only when the
  // derived numbers actually change — not on every render.
  React.useEffect(() => {
    onEvidenceSummaryChange?.(evidenceSummary);
  }, [evidenceSummary, onEvidenceSummaryChange]);

  // "Next gap to close" — the console jump card target. Same SSOT rows
  // as the lanes, so the pick can never point at a row the user can't
  // see in the list below.
  const nextGap = React.useMemo(() => pickNextGap(rows), [rows]);

  // Lane projection — domain groups + per-lane status counts + display
  // ordering ("gaps first" floats Missing/Tentative to the top of each
  // lane; other modes keep registry order).
  const lanes = React.useMemo(() => {
    return groupRequirementsByDomain(rows).map(group => {
      const counts: Record<CoverageCellStatus, number> = {
        strong: 0, adequate: 0, authored: 0, tentative: 0, missing: 0, waived: 0,
      };
      for (const r of group.items) counts[r.best.status] += 1;
      const laneGaps = counts.tentative + counts.missing;
      const filled = group.items.reduce((sum, row) => sum + (row.fill?.filled ?? 0), 0);
      const total = group.items.reduce((sum, row) => sum + (row.fill?.total ?? 0), 0);
      const filledPct = total > 0 ? Math.round((filled / total) * 100) : null;
      const items = [...group.items].sort((a, b) => {
        const ra = STATUS_RANK[a.best.status];
        const rb = STATUS_RANK[b.best.status];
        // Ascending rank = most urgent first (Missing=0 → Strong=4)
        if (ra !== rb) return ra - rb;
        // Within same status: critical severity first
        const ca = a.item.severity === 'critical' ? 0 : 1;
        const cb = b.item.severity === 'critical' ? 0 : 1;
        return ca - cb;
      });
      return {
        domain: group.domain,
        label: group.label,
        items,
        counts,
        gapCount: laneGaps,
        filled, total, filledPct,
      };
    });
  }, [rows]);
  type Lane = (typeof lanes)[number];

  const query = searchQuery.trim().toLowerCase();
  const rowMatchesSearch = React.useCallback(
    (item: ChecklistItem) => {
      if (!query) return true;
      return item.label.toLowerCase().includes(query)
        || (item.description ?? '').toLowerCase().includes(query)
        || item.id.toLowerCase().includes(query);
    },
    [query],
  );
  const rowVisible = React.useCallback(
    (row: Row) => {
      if (!rowMatchesSearch(row.item)) return false;
      if (simpleFacts) {
        if (screeningFilter === 'unknown') return !row.fill || row.fill.filled < row.fill.total;
        if (screeningFilter === 'conflicts') return unresolvedConflictsForRequirement(sed, row.item.id).length > 0;
        return true;
      }
      if (showGoBlockingOnly) {
        return row.best.status === 'missing' && row.item.severity === 'critical';
      }
      return statusFilter === null || statusFilter.has(row.best.status);
    },
    [statusFilter, rowMatchesSearch, showGoBlockingOnly, decisionStage, screeningFilter, sed],
  );
  // Lane collapse: search and the 'complete' filter force lanes open
  // (the user explicitly asked to see those rows); otherwise the user
  // override wins, defaulting to "gaps expand, complete lanes collapse".
  const laneExpandedFor = React.useCallback(
    (lane: Lane) => {
      if (query || (simpleFacts && screeningFilter !== 'all')) return true;
      if (showGoBlockingOnly) return true;
      if (statusFilter !== null) return true;
      return laneOverrides[lane.domain] ?? (simpleFacts || lane.gapCount > 0);
    },
    [query, statusFilter, showGoBlockingOnly, laneOverrides, decisionStage, screeningFilter],
  );
  const toggleLane = React.useCallback(
    (lane: Lane) => {
      setLaneOverrides(prev => ({
        ...prev,
        [lane.domain]: !laneExpandedFor(lane),
      }));
    },
    [laneExpandedFor],
  );

  // Ordered ids of rows the user can currently SEE — the J/K traversal
  // sequence (collapsed lanes + filtered rows are skipped).
  const visibleSequence = React.useMemo(() => {
    const ids: string[] = [];
    for (const lane of lanes) {
      const items = lane.items.filter(rowVisible);
      if (items.length === 0) continue;
      if (!laneExpandedFor(lane)) continue;
      for (const r of items) ids.push(r.item.id);
    }
    return ids;
  }, [lanes, rowVisible, laneExpandedFor]);

  const focusRow = React.useCallback((id: string) => {
    const el = rowRefs.current.get(id);
    el?.focus();
    el?.scrollIntoView?.({ block: 'nearest' });
  }, []);

  const moveSelection = React.useCallback(
    (delta: number) => {
      if (visibleSequence.length === 0) return;
      const idx = selectedRequirementId ? visibleSequence.indexOf(selectedRequirementId) : -1;
      const next = idx === -1
        ? (delta > 0 ? 0 : visibleSequence.length - 1)
        : Math.max(0, Math.min(visibleSequence.length - 1, idx + delta));
      const id = visibleSequence[next];
      if (!id) return;
      setSelectedRequirementId(id);
      focusRow(id);
    },
    [visibleSequence, selectedRequirementId, focusRow],
  );

  // Keyboard triage — J/K (or arrows) move the selection; Enter /
  // ArrowRight / Space open the worksheet. Scoped to the lanes
  // container so the search box and worksheet inputs never lose keys.
  const handleLanesKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      if (target.closest('input, textarea, select')) return;
      const key = e.key;
      if (key === 'j' || key === 'J' || key === 'ArrowDown') {
        e.preventDefault();
        moveSelection(1);
        return;
      }
      if (key === 'k' || key === 'K' || key === 'ArrowUp') {
        e.preventDefault();
        moveSelection(-1);
        return;
      }
      if (key === 'Enter' || key === 'ArrowRight' || key === ' ') {
        const rowEl = target.closest<HTMLElement>('[data-requirement-row]');
        const id = rowEl?.getAttribute('data-requirement-row');
        if (id) selectRequirement(id);
        // Enter/Space on a lane toggle must keep its native button
        // behaviour — only hijack when a row has focus (or ArrowRight,
        // which always means "go to the worksheet").
        if (id || key === 'ArrowRight') {
          e.preventDefault();
          worksheetRef.current?.focus();
        }
      }
    },
    [moveSelection],
  );

  const jumpToNextGap = React.useCallback(() => {
    if (!nextGap) return;
    const id = nextGap.item.id;
    // Gap rows may be hidden — fall back to show all view if needed.
    if (statusFilter !== null && !statusFilter.has(nextGap.best.status)) setStatusFilter(null);
    setLaneOverrides(prev =>
      prev[nextGap.domain] === true ? prev : { ...prev, [nextGap.domain]: true },
    );
    setSelectedRequirementId(id);
    window.requestAnimationFrame(() => focusRow(id));
  }, [nextGap, focusRow, statusFilter]);

  function groupStatusSummary(groupItems: readonly Row[]): string {
    let strong = 0;
    let missing = 0;
    for (const r of groupItems) {
      if (r.best.status === 'strong') strong += 1;
      if (r.best.status === 'missing') missing += 1;
    }
    const parts = [`${groupItems.length} req`];
    if (strong > 0) parts.push(`${strong} strong`);
    if (missing > 0) parts.push(`${missing} missing`);
    return parts.join(' · ');
  }

  // CAF-3 — tier-attributed row tuples for `<CoverageTierFooter>`. Each
  // row's tier is derived from its `RequirementLineage` (status + sedSlot
  // status walk). The aggregator excludes 'none' tier rows so a site
  // with zero autofills doesn't render an empty footer.
  const tierAttributedRows = React.useMemo(
    () => rows.map(r => ({
      status: r.lineage.status,
      tier: attributeRowTier(r.lineage) as CoverageRowTier,
    })),
    [rows],
  );

  if (rows.length === 0) {
    return (
      <div
        className="border border-dashed border-border-subtle p-3 text-[10px] text-text-muted italic"
        data-testid="coverage-matrix-empty"
      >
        No checklist requirements available for the current report scope.
      </div>
    );
  }

  return (
    <section className="space-y-3" data-testid="coverage-matrix">
      {decisionStage != null && !hideStageSelector && (
        <EvidenceStageSelector value={decisionStage} onChange={changeReviewStage} />
      )}
      {/* ── Readiness console head ───────────────────────────────────
          SUPPRESSED when the Evidence Ledger is mounted above this
          matrix. Both consoles answer "how ready is this site?" and they
          answered it DIFFERENTLY on the same screen: the ring's
          `covered = total − (tentative + missing)` reported 80% READY on
          Redhead while the ledger's field-fill bar reported 46%, because
          the ring counts a zero-source, zero-field "Adequate" row as
          covered. Two contradicting gauges 40px apart is worse than the
          single wrong one we started with, so the ledger owns the
          headline and this becomes purely a browse surface. */}
      {!hideReadinessConsole && !simpleFacts && (
      <div
        className="relative overflow-hidden rounded-lg border border-border bg-card shadow-card"
        data-testid="coverage-readiness-console"
      >
        {/* Thin status distribution strip on the top edge. */}
        <div className="absolute inset-x-0 top-0 flex h-0.5" data-testid="coverage-readiness-strip" aria-hidden="true">
          {CELL_STATUS_ORDER.map(s => (summary.counts[s] > 0 ? (
            <span
              key={s}
              className={CELL_BAR_CLASS[s]}
              style={{ width: `${(summary.counts[s] / rows.length) * 100}%` }}
            />
          ) : null))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4 p-4 sm:p-5">
          <div className="flex min-w-0 items-center gap-4">
            <ReadinessRing covered={covered} total={rows.length} pct={readyPct} />
            <div className="min-w-0 space-y-1">
              <div className="flex items-center gap-2">
                <div className="text-[13px] font-semibold text-text-primary" data-testid="coverage-readiness-headline">
                  {summary.counts.strong} of {rows.length} requirements at{' '}
                  <span className="text-success">Strong</span>
                </div>
                {sed?.version && (
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${
                        isStaleSed
                          ? 'border-warning/40 bg-warning/10 text-warning'
                          : 'border-border-subtle bg-surface-2 text-text-secondary'
                      }`}
                      title={isStaleSed ? 'SED is out of date — sources are newer than this build, or a section has passed its refresh window.' : 'Site Evidence Document Version'}
                      data-testid="coverage-sed-version"
                      data-stale={isStaleSed ? 'true' : 'false'}
                    >
                      SED v{sed.version}
                    </span>
                    {isStaleSed && (
                      onRebuildSed ? (
                        <button
                          type="button"
                          onClick={onRebuildSed}
                          disabled={isRebuilding}
                          className="inline-flex items-center gap-1 rounded border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-warning hover:bg-warning/20 disabled:opacity-50 transition-colors"
                          title="SED is out of date. Click to rebuild."
                          data-testid="coverage-stale-sed-tag"
                        >
                          {isRebuilding ? (
                            <>
                              <RefreshCw className="h-2.5 w-2.5 animate-spin" aria-hidden="true" />
                              Rebuilding…
                            </>
                          ) : (
                            <>
                              <AlertTriangle className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
                              Out of date — rebuild
                            </>
                          )}
                        </button>
                      ) : (
                        // No rebuild handler wired (legacy host) — still surface
                        // the staleness so the user isn't misled into trusting a
                        // stale SED, even when the rebuild affordance is absent.
                        <span
                          className="inline-flex items-center gap-1 rounded border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-warning"
                          title="SED is out of date."
                          data-testid="coverage-stale-sed-tag"
                        >
                          <AlertTriangle className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
                          Out of date
                        </span>
                      )
                    )}
                  </div>
                )}
                {/* SED rebuilt-at freshness chip — neutral StatusPill (NOT
                    hand-rolled uppercase chrome, which the design-tokens gate
                    forbids). Reads `site.lastBootstrapAt` (stamped by
                    bootstrapSiteSedServer) and renders relative time via the
                    shared portfolio formatter, so "SED rebuilt 3h ago" stays
                    consistent with the wizard freshness line. Omitted when the
                    timestamp is missing (legacy sites). */}
                {sedRebuiltAt && (
                  <StatusPill
                    variant="neutral"
                    icon={History}
                    className="px-1.5 text-[9px]"
                    data-testid="coverage-sed-freshness"
                  >
                    <span title={`SED rebuilt ${new Date(sedRebuiltAt).toLocaleString()}`}>
                      SED rebuilt {formatRelativeTime(sedRebuiltAt)}
                    </span>
                  </StatusPill>
                )}
              </div>
              <p className="type-caption text-text-muted">
                Reach <span className="font-semibold text-text-primary">Strong</span> on the amber
                items to move this site to investment-grade.
              </p>
              <div className="flex flex-wrap gap-x-3.5 gap-y-1 pt-1" data-testid="coverage-readiness-mix">
                {CELL_STATUS_ORDER.map(s => (
                  <span
                    key={s}
                    className="inline-flex items-center gap-1.5 type-caption text-text-secondary"
                    data-testid={`coverage-readiness-mix-${s}`}
                  >
                    <i className={`h-2 w-2 rounded-[2px] ${CELL_BAR_CLASS[s]}`} aria-hidden="true" />
                    <span className="tabular-nums">{summary.counts[s]}</span> {CELL_LABEL[s]}
                  </span>
                ))}
              </div>
            </div>
          </div>
          {nextGap ? (
            <button
              type="button"
              onClick={jumpToNextGap}
              className="group min-w-[230px] rounded-md border border-border-strong bg-surface-3 p-3 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              data-testid="coverage-next-gap"
              data-requirement-id={nextGap.item.id}
              title={nextGap.item.description}
            >
              <span className="flex items-center gap-1.5 type-section text-warning">
                <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
                Next gap to close
              </span>
              <span className="mt-1 block truncate text-[13px] font-semibold text-text-primary">
                {nextGap.item.label}
              </span>
              <span className="mt-1.5 flex items-center gap-1 type-caption text-primary">
                Jump to requirement
                <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
              </span>
            </button>
          ) : (
            <div
              className="min-w-[230px] rounded-md border border-success/40 bg-success/5 p-3"
              data-testid="coverage-next-gap-clear"
            >
              <span className="type-section text-success">No gaps to close</span>
              <span className="mt-1 block type-caption text-text-secondary">
                Every requirement in scope has evidence.
              </span>
            </div>
          )}
        </div>
      </div>
      )}

      {simpleFacts && (
        <div className="flex flex-wrap items-start justify-between gap-2 border-l-2 border-primary/60 bg-primary/5 px-3 py-2.5"
          data-testid="coverage-screening-summary">
          <div className="max-w-prose space-y-1">
            <p className="type-body font-semibold text-text-primary">{decisionStage === 1 ? 'Facts for screening' : 'Facts for validation'}</p>
            <p className="type-caption text-text-secondary">{decisionStage === 1 ? 'Fields fill automatically from documents and site data. Unknowns can be carried into the screening report.' : 'Review the engineering facts and their sources. Unknowns remain unresolved until the required evidence and reports are current.'}</p>
          </div>
          <p className="type-caption tabular-nums text-text-secondary">
            {evidenceSummary.filled} known · {Math.max(0, evidenceSummary.total - evidenceSummary.filled)} unknown
          </p>
        </div>
      )}

      {/* ── Triage controls ────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3" data-testid="coverage-matrix-controls">
        {simpleFacts ? (
          <div className="flex flex-wrap gap-1 rounded-md border border-border-subtle bg-surface-1 p-1" role="group" aria-label="Screening evidence filter">
            {([
              { id: 'all', label: 'All topics' },
              { id: 'unknown', label: 'Incomplete fields' },
              { id: 'conflicts', label: 'Source conflicts' },
            ] as const).map(filter => (
              <button key={filter.id} type="button" aria-pressed={screeningFilter === filter.id}
                onClick={() => setScreeningFilter(filter.id)} data-testid={`coverage-screening-filter-${filter.id}`}
                className={`rounded px-2.5 py-1 type-caption transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${screeningFilter === filter.id ? 'bg-primary/15 text-primary' : 'text-text-secondary hover:bg-state-hover'}`}>
                {filter.label}
              </button>
            ))}
          </div>
        ) : (
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Status filter">
          {CELL_STATUS_ORDER.map(s => {
            const count = summary.counts[s];
            const active = statusFilter?.has(s) ?? false;
            const isZero = count === 0;
            return (
              <button
                key={s}
                type="button"
                aria-pressed={active}
                disabled={isZero}
                onClick={() => toggleStatusFilter(s)}
                data-testid={`coverage-filter-${s}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 type-caption transition-colors
                  ${isZero ? 'cursor-default opacity-40 border-border' : 'cursor-pointer border-border hover:bg-state-hover'}
                  ${active ? 'bg-primary/15 border-primary/40 text-primary' : 'text-text-secondary'}
                `}
              >
                <span className={`h-2 w-2 rounded-full ${CELL_DOT_CLASS[s]}`} aria-hidden="true" />
                {CELL_LABEL[s]}
                <span className="font-normal tabular-nums opacity-70" data-testid={`coverage-filter-${s}-count`}>
                  {count}
                </span>
              </button>
            );
          })}
          {summary.criticalMissing.length > 0 && (
            <div className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
          )}
          {summary.criticalMissing.length > 0 && (
            <button
              type="button"
              aria-pressed={showGoBlockingOnly}
              onClick={() => setShowGoBlockingOnly(p => !p)}
              data-testid="coverage-filter-go-blocking"
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 type-caption transition-colors
                ${showGoBlockingOnly ? 'bg-warning/15 border-warning/40 text-warning' : 'border-border hover:bg-state-hover text-warning cursor-pointer'}
              `}
            >
              <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
              {simpleFacts ? 'Screening gaps' : 'GO-blocking'}
              <span className="font-normal tabular-nums opacity-70">
                {summary.criticalMissing.length}
              </span>
            </button>
          )}
          {(statusFilter !== null || showGoBlockingOnly) && (
            <button
              type="button"
              onClick={() => {
                setStatusFilter(null);
                setShowGoBlockingOnly(false);
              }}
              className="type-caption text-text-muted underline hover:text-text-primary"
            >
              Show all
            </button>
          )}
        </div>
        )}
        <label className="relative min-w-[200px] flex-1">
          <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-text-muted" aria-hidden="true" />
          <input
            type="search"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={simpleFacts ? 'Search facts…' : 'Search requirements…'}
            aria-label="Search requirements"
            data-testid="coverage-matrix-search"
            className="w-full rounded-md border border-border bg-background py-1.5 pl-7 pr-2 text-xs text-text-primary placeholder:text-text-disabled focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          />
        </label>
        {onRebuildSed && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-[11px] shrink-0"
            disabled={isRebuilding}
            onClick={() => onRebuildSed()}
            data-testid="sed-rebuild-button"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${isRebuilding ? 'animate-spin' : ''}`} />
            {isRebuilding ? 'Refreshing & rebuilding…' : 'Refresh & rebuild'}
          </Button>
        )}
      </div>

      <div
        className="grid w-full grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,0.9fr)]"
        data-testid="coverage-matrix-master-detail"
      >
      <div className="w-full min-w-0 space-y-2" data-testid="coverage-matrix-scroll">
        <div
          role="group"
          aria-label="Coverage requirements"
          data-testid="coverage-matrix-lanes"
          onKeyDown={handleLanesKeyDown}
        >
          {lanes.map(lane => {
            const visibleItems = lane.items.filter(rowVisible);
            const laneHidden = visibleItems.length === 0;
            const expanded = laneExpandedFor(lane);
            return (
              <section
                key={lane.domain}
                hidden={laneHidden}
                className="mb-3 overflow-hidden rounded-lg border border-border bg-surface-1 last:mb-0"
                data-testid={`coverage-matrix-group-${lane.domain}`}
                data-expanded={expanded}
              >
                <button
                  type="button"
                  onClick={() => toggleLane(lane)}
                  aria-expanded={expanded}
                  aria-controls={`coverage-lane-rows-${lane.domain}`}
                  data-testid={`coverage-matrix-lane-toggle-${lane.domain}`}
                  className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-3.5 py-2.5 text-left transition-colors hover:bg-state-hover/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary/60"
                >
                  <ChevronDown
                    className={`h-3.5 w-3.5 text-text-muted transition-transform duration-200 motion-reduce:transition-none ${expanded ? 'rotate-0' : '-rotate-90'}`}
                    aria-hidden="true"
                  />
                  <span className="flex min-w-0 items-baseline gap-2.5">
                    <span className="type-section text-text-primary">{lane.label}</span>
                    <span className="truncate type-caption text-text-muted">
                      {simpleFacts ? `${lane.items.length} topics` : groupStatusSummary(lane.items)}
                    </span>
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="hidden h-1.5 w-32 overflow-hidden rounded-sm border border-border-subtle bg-surface-3 sm:flex"
                      data-testid={`coverage-matrix-lane-bar-${lane.domain}`}
                      title={`${lane.filled}/${lane.total} fields filled`}>
                      <i className="h-full bg-primary" style={{ width: `${lane.filledPct ?? 0}%` }} />
                    </span>
                    <span className="min-w-20 text-right font-mono text-[11px] tabular-nums text-text-secondary"
                      data-testid={`coverage-matrix-lane-completeness-${lane.domain}`}
                      title={`${lane.filled}/${lane.total} fields filled; evidence quality is shown on each requirement`}>
                      {simpleFacts ? `${lane.filled}/${lane.total} fields` : lane.filledPct == null ? 'No fields' : `${lane.filledPct}% filled`}
                    </span>
                  </span>
                </button>
                <div
                  id={`coverage-lane-rows-${lane.domain}`}
                  hidden={!expanded}
                  className="border-t border-border-subtle"
                >
                  {lane.items.map(row => {
                    const { item, best, lineage, fill } = row;
                    const visible = rowVisible(row);
                    const isSelected = selectedRequirementId === item.id;
                    const isCritical = item.severity === 'critical';
                    const conflictCount = sed
                      ? unresolvedConflictsForRequirement(sed, item.id).length
                      : 0;
                    // SED-FOUNDATION — read-only workflow rollup for this row's
                    // SED sections. Nothing renders when the SED has no workflows.
                    const workflowRollup = workflowSummary.isEmpty
                      ? null
                      : rollupSectionsForRequirement(workflowSummary, item.sedSectionsHelpful);
                    const fillPct = fill && fill.total > 0
                      ? Math.min(100, Math.round((fill.filled / fill.total) * 100))
                      : 0;
                    return (
                      <div
                        key={item.id}
                        role="button"
                        tabIndex={isSelected ? 0 : -1}
                        hidden={!visible}
                        ref={el => {
                          if (el) rowRefs.current.set(item.id, el);
                          else rowRefs.current.delete(item.id);
                        }}
                        data-testid={`coverage-matrix-row-${item.id}`}
                        data-requirement-row={item.id}
                        aria-pressed={isSelected}
                        onClick={() => selectRequirement(item.id)}
                        title={item.description}
                        className={`grid cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2.5 border-b border-l-2 border-b-border-subtle px-3 py-2 transition-colors last:border-b-0 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary/60 ${
                          isSelected
                            ? 'border-l-primary bg-primary/5'
                            : 'border-l-transparent hover:bg-state-hover/40'
                        }`}
                      >
                        <span className="flex items-center gap-1.5" data-testid={`coverage-matrix-best-${item.id}`} data-status={best.status}>
                          <span
                            className={`flex h-[22px] w-[22px] items-center justify-center rounded-md border text-[12px] font-bold leading-none ${simpleFacts ? 'border-border-subtle text-text-secondary' : CELL_CLASS[best.status]}`}
                            title={simpleFacts ? (fill?.filled ? 'Facts available' : 'Unknowns recorded in screening') : CELL_LABEL[best.status]}
                            data-testid={`coverage-matrix-best-chip-${item.id}`}
                          >
                            {simpleFacts ? (fill?.filled ? '·' : '—') : CELL_GLYPH[best.status]}
                          </span>
                          {!simpleFacts && isCritical && isGapRow(row) && (
                            <span
                              className="inline-flex items-center rounded px-1 py-0.5 type-caption bg-warning/10 text-warning border border-warning/20"
                              title={simpleFacts
                                ? 'Unresolved screening context. Conditional screening can continue; final GO validation is reviewed in Stage 2.'
                                : 'This critical gap reduces evidence confidence and may withhold the Go/No-Go verdict'}
                            >
                              {simpleFacts ? 'Review' : '⚠ Go'}
                            </span>
                          )}
                        </span>
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-center gap-1">
                            {isCritical && !simpleFacts && isGapRow(row) && (
                              <AlertTriangle
                                className="mt-px h-2.5 w-2.5 shrink-0 text-danger"
                                aria-label="Critical requirement"
                              />
                            )}
                            <span className="break-words text-xs leading-snug text-text-primary">{item.label}</span>
                            {conflictCount > 0 && (
                              <span
                                className="shrink-0 rounded-sm border border-warning/40 bg-warning/10 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-warning"
                                data-testid={`coverage-matrix-conflicts-${item.id}`}
                                title={`${conflictCount} unresolved evidence conflict${conflictCount === 1 ? '' : 's'}`}
                              >
                                {simpleFacts ? 'Source conflict' : 'UFC'} {conflictCount}
                              </span>
                            )}
                            {!simpleFacts && workflowRollup && workflowRollup.openQuestions > 0 && (
                              <span
                                className="shrink-0 rounded-sm border border-warning/40 bg-warning/10 px-1 py-0.5 text-[9px] font-semibold text-warning"
                                data-testid={`coverage-matrix-openq-${item.id}`}
                                title={`${workflowRollup.openQuestions} open verification question${workflowRollup.openQuestions === 1 ? '' : 's'} — lowers readiness, does not block`}
                              >
                                ? {workflowRollup.openQuestions}
                              </span>
                            )}
                            {!simpleFacts && workflowRollup && workflowRollup.verifiedConfirmed > 0 && (
                              <span
                                className="shrink-0 rounded-sm border border-success/40 bg-success/10 px-1 py-0.5 text-[9px] font-semibold text-success"
                                data-testid={`coverage-matrix-verified-${item.id}`}
                                title={`${workflowRollup.verifiedConfirmed} verified with evidence`}
                              >
                                ✓ {workflowRollup.verifiedConfirmed}
                              </span>
                            )}
                          </span>
                          {!simpleFacts && (() => {
                            const hint = item.kind === 'site-intent'
                              ? 'Review project context in Intent. Undecided choices do not prevent conditional screening.'
                              : simpleFacts
                                ? 'Use known facts; missing evidence remains unknown in the report.'
                                : rowActionHint(best.status, best.matchCount, fill, isCritical);
                            return hint ? (
                              <span className="block type-caption text-text-muted mt-0.5 leading-snug">{hint}</span>
                            ) : null;
                          })()}
                          <span className="mt-0.5 block truncate type-caption text-text-muted">
                            {simpleFacts && fill
                              ? `${fill.filled} known · ${Math.max(0, fill.total - fill.filled)} unknown`
                              : rowSubtitle(best, lineage)}
                          </span>
                        </span>
                        <span className="flex items-center gap-2">
                          <span
                            className={simpleFacts ? 'hidden' : 'hidden items-center gap-1.5 sm:flex'}
                            data-testid={`coverage-matrix-meter-${item.id}`}
                          >
                            <span className="h-[5px] w-16 overflow-hidden rounded-sm border border-border-subtle bg-surface-3">
                              <i
                                className={`block h-full ${simpleFacts ? 'bg-primary' : CELL_BAR_CLASS[best.status]}`}
                                style={{ width: `${fillPct}%` }}
                              />
                            </span>
                            <span className="w-8 text-right font-mono text-[10px] tabular-nums text-text-muted">
                              {fill ? `${fill.filled}/${fill.total}` : '—'}
                            </span>
                          </span>
                          <ChevronRight
                            className={`h-3.5 w-3.5 shrink-0 transition-colors ${isSelected ? 'text-primary' : 'text-text-disabled'}`}
                            aria-hidden="true"
                          />
                        </span>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
        <p className="type-caption text-text-muted" data-testid="coverage-matrix-kbd-hint">
          <kbd className={KBD_CLASS}>J</kbd>/<kbd className={KBD_CLASS}>K</kbd> move rows
          {' · '}
          <kbd className={KBD_CLASS}>→</kbd>/<kbd className={KBD_CLASS}>Enter</kbd> open worksheet
        </p>
      </div>

      <div
        className="min-w-0 focus:outline-none xl:sticky xl:top-4 xl:self-start"
        data-testid="coverage-matrix-detail-column"
        ref={worksheetRef}
        tabIndex={-1}
      >
      <CoverageRequirementPanel
        authoringItem={canonicalRequirements.find(requirement => requirement.id === selectedRow?.item.id)}
        sourceDetails={selectedRow && renderTopicSourceDetails ? renderTopicSourceDetails(selectedRow.item.id) : undefined}
        onPreviousTopic={visibleSequence.indexOf(selectedRequirementId ?? '') > 0 ? () => moveSelection(-1) : undefined}
        onNextTopic={visibleSequence.indexOf(selectedRequirementId ?? '') >= 0 && visibleSequence.indexOf(selectedRequirementId ?? '') < visibleSequence.length - 1 ? () => moveSelection(1) : undefined}
        item={selectedRow?.item ?? null}
        status={selectedRow?.best.status ?? 'missing'}
        authoredClaims={selectedRow?.best.authoredClaims}
        lineage={selectedRow?.lineage ?? null}
        sed={sed}
        coverage={selectedRow ? coverageByRequirement?.[selectedRow.item.id] : undefined}
        matchCount={selectedRow?.best.matchCount}
        topMatch={selectedRow?.best.topMatch}
        onOpenAuthor={() => {
          if (!selectedRow) return;
          onOpenAuthorFor?.(selectedRow.item.id);
          if (!hostOwnsAuthorDialog) {
            setAuthoringFor(selectedRow.item);
          }
        }}
        onOpenUploadFor={() => selectedRow && onOpenUploadFor(selectedRow.item.id)}
        onOpenInDocuments={() => selectedRow && onOpenInDocuments(selectedRow.item.id)}
        onOpenBaseline={onOpenBaseline ? () => selectedRow && onOpenBaseline(selectedRow.item.id) : undefined}
        onAddSupportingDoc={
          selectedRow
            ? () => (onAddSupportingDoc
              ? onAddSupportingDoc(selectedRow.item.id)
              : onOpenUploadFor(selectedRow.item.id))
            : undefined
        }
        onReplaceDoc={
          selectedRow && onReplaceDoc && selectedRow.best.topMatch
            ? () => onReplaceDoc(selectedRow.best.topMatch!.doc.id, selectedRow.item.id)
            : undefined
        }
        onOpenLineage={handleOpenLineage}
        onViewVariables={
          selectedRow && onViewVariables
            ? (fieldPath?: string) => onViewVariables(selectedRow.item.id, fieldPath)
            : undefined
        }
        focusVariablePath={panelFocusVariablePath}
        forceExpandVariables={forceExpandRequirementVariables}
        siteContext={siteContext}
        documents={documents}
        siteId={siteId}
        onOpenDocument={onOpenDocument}
        onReloadSed={onReloadSed}
        autoFillEntry={selectedRow ? effectiveAutoFillRegistry[selectedRow.item.id] : undefined}
        onAuthorSaved={onAuthoredClaimSaved}
        reportTypes={effectiveTypes}
        projectIntent={projectIntent ?? undefined}
        typedPreferences={typedPreferences}
        onOpenSiteIntent={onOpenSiteIntent}
        decisionStage={decisionStage}
        onWaiveRequirement={onWaiveRequirement}
        unifiedEvidencePanel={unifiedEvidencePanel}
      />
      </div>
      </div>

      {!simpleFacts && (
      <div
        className="space-y-1.5 border-t border-border-subtle pt-2 text-[11px] text-text-muted"
        data-testid="coverage-matrix-summary"
      >
        <p
          className="flex flex-wrap items-center gap-x-3.5 gap-y-1 type-caption text-text-muted"
          data-testid="coverage-matrix-legend"
        >
          {CELL_STATUS_ORDER.map(s => (
            <span key={s} className="inline-flex items-center gap-1.5">
              <i className={`h-2 w-2 rounded-[2px] ${CELL_BAR_CLASS[s]}`} aria-hidden="true" />
              {CELL_GLYPH[s]} {CELL_LABEL[s]}
            </span>
          ))}
        </p>
        <span data-testid="coverage-matrix-summary-counts">
          {rows.length} requirements
          {' · '}
          {summary.counts.strong} strong
          {' · '}
          {summary.counts.missing} missing
        </span>
        {summary.criticalMissing.length > 0 && (
          <span
            className={`block ${simpleFacts ? 'text-text-muted' : 'text-danger'}`}
            data-testid="coverage-matrix-summary-critical"
          >
            {simpleFacts ? 'Screening context still unresolved: ' : 'Critical missing: '}
            {summary.criticalMissing.map(r => r.item.label).join(', ')}.
            {simpleFacts && ' Conditional screening can use the other available evidence; formal rights and final GO validation remain unresolved.'}
          </span>
        )}
        <CoverageTierFooter rows={tierAttributedRows} totalRows={rows.length} />
        {!workflowSummary.isEmpty && (
          <span
            className="block text-text-muted"
            data-testid="coverage-matrix-workflow-summary"
          >
            Evidence workflow:
            {' '}
            {workflowSummary.totalOpenQuestions} open question{workflowSummary.totalOpenQuestions === 1 ? '' : 's'}
            {' · '}
            {workflowSummary.totalVerifiedConfirmed} verified
            {workflowSummary.totalSitingNotes > 0
              ? ` · ${workflowSummary.totalSitingNotes} siting note${workflowSummary.totalSitingNotes === 1 ? '' : 's'}`
              : ''}
            {workflowSummary.totalOpenQuestions > 0 ? ' — open questions lower readiness but do not block generation.' : ''}
          </span>
        )}
      </div>

      )}

      {/* UAE-2 — internal AuthorEvidenceDialog. Mounted lazily so the
          dialog code is only paid for when the user opens it. The host
          can still listen via `onOpenAuthorFor` (e.g. to record a
          breadcrumb) without taking responsibility for the dialog.

          EVL-2 — pre-fill from the requirement's lineage. We re-resolve
          the lineage at open-time (rather than passing the row's
          memoised value through) so the dialog always seeds from the
          latest evidence — providers or docs may have landed between
          the matrix render and the click. */}
      {authoringFor && !hostOwnsAuthorDialog && (
        <AuthorEvidenceDialog
          siteId={siteId}
          requirementId={authoringFor.id}
          requirementLabel={authoringFor.label}
          schema={canonicalRequirements.find(item => item.id === authoringFor.id)?.authoredSchema ?? authoringFor.authoredSchema ?? []}
          decisionStage={decisionStage}
          existing={(authoredClaims ?? []).find(c => c.requirementId === authoringFor.id)}
          prefillFromLineage={
            getRequirementLineage(authoringFor.id, {
              site: { siteContext: { claims: authoredClaims ?? [] }, documents },
              sed,
              reportType: effectiveTypes[0] ?? 'feasibility',
              scopeReportTypes: effectiveTypes,
              dam: dam ?? undefined,
              matchByType,
              priorReports: priorReports ? Array.from(priorReports) : undefined,
              requirementLabel: authoringFor.label,
              checklistItem: authoringFor,
            })
          }
          onOpenLineage={handleOpenLineage}
          sed={sed}
          documents={documents}
          onClose={() => setAuthoringFor(null)}
          onSaved={(result) => {
            onAuthoredClaimSaved?.({ claimId: result.claimId, requirementId: authoringFor.id });
          }}
        />
      )}

      {/* EVL-4 / EVL-5 — internal RequirementLineageInspector. Mounted
          only when the user clicks the chip strip AND the host didn't
          override via `onOpenLineage`. The Generation tab answers
          *"what reaches the LLM for this requirement"* per the user's
          direct ask. */}
      {inspectingLineage && (
        <RequirementLineageInspector
          open
          onClose={() => setInspectingLineage(null)}
          lineage={inspectingLineage}
          sedText={sedText}
        />
      )}

    </section>
  );
};
